import { Map, OrderedMap } from 'immutable';

export const MAX_DATE_RANGE = 30 * 24 * 60 * 60; // 30 days
export const ONE_HOUR = 60 * 60 * 1000;
export const ONE_DAY = 24 * ONE_HOUR;
export const DEFAULT_FILTER = Object.freeze({ owners: ['myself'] });
export const COMMON_ERRORS_URL = 'https://docs.saucelabs.com/dev/error-messages/index.html';
export const TEST_STATUS_INFO_URL = Object.freeze({
  url: 'https://docs.saucelabs.com/basics/test-config-annotation/test-annotation/index.html',
  label: 'More Info On Status',
});

export const TESTS_REQUEST_SIZE = 10;
export const TESTS_BY_ERROR_LIMIT = 20;
export const TEST_ERRORS_PER_PAGE = 20;

export const STATUS_KEYS = ['passed', 'failed', 'error', 'complete'];
export const TEST_KEYS = ['count'];
export const TEST_START_TIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';
export const TEST_START_TIME_MINUTE_FORMAT = 'yyyy-MM-dd HH:mm';
export const TEST_START_DATE_FORMAT = 'yyyy-MM-dd';
export const TIME_AND_DATE_FORMAT = 'MMM-d H:mm';
export const DATE_ONLY_FORMAT = 'MMM-d';
export const TESTS_CHART_SIZE = Object.freeze({
  height: 250,
  width: '100%',
});
export const CHART_BREAKPOINT_DAYS = 2;

export const CHARTS_PEAK_ACTIVE_COLOR = 'green';
export const CHARTS_PEAK_PAST_COLOR = 'greenGray';
export const CHARTS_TESTS_ACTIVE_COLOR = 'darkBlue';
export const CHARTS_TESTS_PAST_COLOR = 'darkBlueGray';

export const VM_CONCURRENCY_DATA_KEYS = [
  Object.freeze({ name: 'peak_vm_ccy1', color: CHARTS_PEAK_PAST_COLOR, legendPrefix: 'Previous' }),
  Object.freeze({ name: 'peak_vm_ccy2', color: CHARTS_PEAK_ACTIVE_COLOR, legendPrefix: 'Last' }),
];

export const TEST_CONCURRENCY_DATA_KEYS = [
  Object.freeze({ name: 'tests_by_day1', color: CHARTS_TESTS_PAST_COLOR, legendPrefix: 'Previous' }),
  Object.freeze({ name: 'tests_by_day2', color: CHARTS_TESTS_ACTIVE_COLOR, legendPrefix: 'Last' }),
];

export const STATUSES = Object.freeze({
  error: Object.freeze({
    color: '#f68d38',
    value: 'error',
  }),
  passed: Object.freeze({
    color: '#70be44',
    value: 'passed',
  }),
  complete: Object.freeze({
    color: '#ccc',
    value: 'complete',
  }),
  failed: Object.freeze({
    color: '#e2231a',
    value: 'failed',
  }),
  tests: Object.freeze({
    color: '#1395f0',
    value: 'jobs',
  }),
  count: Object.freeze({
    color: '#1395f0',
    value: 'jobs',
  }),
});

export const BUILD_STATUS = Object.freeze({
  failed: Object.freeze({
    value: 'failed',
    label: 'Failed jobs only',
    trackEvent: 'BUILD_STATUS_FAILED_TEST_CHECKBOX',
  }),
  error: Object.freeze({
    value: 'error',
    label: 'Error jobs only',
    trackEvent: 'BUILD_STATUS_ERROR_TEST_CHECKBOX',
  }),
});

export const TRENDS_TABLE_COLUMNS = Object.freeze({
  name: Object.freeze({
    name: 'Name',
    key: 'name',
    field: 'name',
  }),
  startTime: Object.freeze({
    name: 'Start Time',
    key: 'start_time',
    field: 'start_time',
  }),
  duration: Object.freeze({
    name: 'Duration',
    key: 'duration',
    field: 'duration',
  }),
  efficiency: Object.freeze({
    name: 'Efficiency',
    key: 'efficiency',
    field: 'efficiency',
  }),
  owner: Object.freeze({
    name: 'Owner',
    key: 'owner',
    field: 'owner',
  }),
  status: Object.freeze({
    name: 'Status',
    key: 'status',
    field: 'status',
  }),
  error: Object.freeze({
    name: 'Error',
    key: 'error',
    field: 'error',
  }),
});

export const OWNER_FILTER_KEYS = Object.freeze({
  OWNERS: 'owners',
  TEAM: 'owner_team',
  USER: 'owner_id_indv',
});

export const OWNER_FILTER_KEYS_V2 = Object.freeze({
  OWNERS: 'owners',
  TEAM: 'team_id',
  USER: 'user_id',
});

export const TEAM_API_DEFAULT_PARAMS = Object.freeze({
  limit: 1000,
  offset: 0,
});

export const OWNER_RADIO_OPTIONS = ['myself', 'organization', 'team'];
export const DEVICE_GROUP_RADIO_NAME = 'Device Group';

export const ACTIVITY_REQUEST_TYPES = Object.freeze({
  SCOPE_MYSELF: 'scope-myself',
  SCOPE_ORG_XTM: 'scope-organization-xtm-admin',
  SCOPE_ORG_USER: 'scope-organization-user',
  SCOPE_MYTEAM: 'scope-myteam',
  OWNERS_USER: 'owners-user',
  OWNERS_TEAM: 'owners-team',
});

export const OWNER_FILTER_SCOPES = Object.freeze({
  ORGANIZATION: 'organization',
  MYSELF: 'myself',
  TEAM: 'team',
});

export const META_SKIP_ERROR_PAGE = { skipErrorPage: true };

export const ROOTCAUSE_QUERY_STRINGS = Object.freeze({
  TEST_HASH: 'testHash',
  PATTERN_ID: 'pattern',
  USER: 'user',
});

export const TESTS_PAGE_SIZES = [10, 20, 30, 50, 100];

export const TESTS_PAGE_STATUSES = Object.freeze({
  all: Object.freeze({
    key: 'all',
    title: 'All',
    path: '/all',
    status: null,
  }),
  failed: Object.freeze({
    key: 'failed',
    title: 'Consistently Failing',
    path: '/failed',
    status: 'failed',
  }),
  passed: Object.freeze({
    key: 'passed',
    title: 'Consistently Passing',
    path: '/passed',
    status: 'passed',
  }),
  error: Object.freeze({
    key: 'error',
    title: 'Consistently Error',
    path: '/error',
    status: 'error',
    statusV2: 'error',
  }),
  complete: Object.freeze({
    key: 'complete',
    title: 'Missing Status',
    path: '/complete',
    status: 'complete',
  }),
  inconsistent: Object.freeze({
    key: 'inconsistent',
    title: 'Inconsistent Result',
    path: '/inconsistent',
    status: 'inconsistent',
  }),
});

export const TESTS_PAGE_TABLE_HEADERS = Object.freeze({
  name: 'Job name',
  total_runs: 'Total runs',
  avg_duration: 'Average duration',
  total_duration: 'Total duration',
  pass_count: 'Pass count',
  pass_rate: 'Pass rate',
  fail_count: 'Fail count',
  fail_rate: 'Fail rate',
  error_count: 'Error count',
  error_rate: 'Error rate',
  complete_count: 'Complete count',
  complete_rate: 'Complete rate',
});

export const TEST_PAGE_HEALTH_SNAPSHOT_LEGEND = Object.freeze({
  failing: Object.freeze({
    status: 'failing',
    link: TESTS_PAGE_STATUSES.failed.path,
  }),
  passing: Object.freeze({
    status: 'passing',
    link: TESTS_PAGE_STATUSES.passed.path,
  }),
  error: Object.freeze({
    status: 'error',
    link: TESTS_PAGE_STATUSES.error.path,
  }),
  missingStatus: Object.freeze({
    status: 'missingStatus',
    link: TESTS_PAGE_STATUSES.complete.path,
  }),
});

export const TEST_CASE_HEALTH_TITLE = 'Job Health Snapshot';
export const TEST_CASE_HEALTH_DESCRIPTION = 'Jobs are grouped by it\'s "unique" name.';

export const DEFAULT_GET_OWNERS_OPTIONS = Object.freeze({
  getOrganizationUsers: true,
  getTeams: true,
});

export const ACTIVITY_TIME_RANGES = OrderedMap({
  'Last 1 day': Map({ type: 'relative', value: 24 * 60 * 60 }),
  'Last 7 days': Map({ type: 'relative', value: 7 * 24 * 60 * 60 }),
  'Last 30 days': Map({ type: 'relative', value: 30 * 24 * 60 * 60 }),
});

export const RELATIVE_TIME_RANGES = OrderedMap({
  'Last 15 mins': Map({ type: 'relative', value: 15 * 60 }),
  'Last 1 hour': Map({ type: 'relative', value: 60 * 60 }),
  'Last 6 hours': Map({ type: 'relative', value: 6 * 60 * 60 }),
  'Last 12 hours': Map({ type: 'relative', value: 12 * 60 * 60 }),
}).merge(ACTIVITY_TIME_RANGES);

export const TrendsType = {
  BROWSER: 'browser',
  OS: 'os',
  DEVICE: 'device',
  FRAMEWORK: 'framework',
};

export const TEST_EMPTY_NAME_DEFAULT = 'Unnamed job';

export const DEFAULT_FILTERS = Object.freeze({
  scope: OWNER_FILTER_SCOPES.ORGANIZATION,
});

export const TEST_LIST_CLOUD_KEY = 'testListCloudType';
export const COVERAGE_CLOUD_TYPE = 'coverageCloudType';
export const TEST_HISTORY_CLOUD_KEY = 'testHistoryCloudType';
export const TEST_OVERVIEW_CLOUD_KEY = 'testOverviewCloudType';
export const TRENDS_CLOUD_KEY = 'trendsCloudType';
export const FAILURE_ANALYSIS_CLOUD_KEY = 'failureAnalysisCloudType';
export const USAGE_ANALYTICS_CLOUD_KEY = 'usageAnalyticsCloudType';

export const CLOUD_TYPE_PARAM_NAME = 'cloudType';
export const FILTER_TYPE_PARAM_NAME = 'type';
export const TEST_OVERVIEW_PATH = `/test-overview/:tabName?/:${CLOUD_TYPE_PARAM_NAME}?/:${FILTER_TYPE_PARAM_NAME}?`;
export const TEST_HISTORY_PATH = `/test-history/:${CLOUD_TYPE_PARAM_NAME}?/:${FILTER_TYPE_PARAM_NAME}?`;

export const STORAGE_ROUTE_MAP = Object.freeze({
  '/analytics/test-overview/overview': 'TestOverview',
  '/analytics/test-overview/tests': 'TestsVDC',
  '/analytics/test-overview/tests/all': 'TestsVDC',
  '/analytics/test-overview/tests/failed': 'TestsVDC',
  '/analytics/test-overview/tests/passed': 'TestsVDC',
  '/analytics/test-overview/tests/error': 'TestsVDC',
  '/analytics/test-overview/tests/complete': 'TestsVDC',
  '/analytics/test-overview/tests/inconsistent': 'TestsVDC',
  '/analytics/test-overview/tests/vdc': 'TestsVDC',
  '/analytics/test-overview/tests/vdc/all': 'TestsVDC',
  '/analytics/test-overview/tests/vdc/failed': 'TestsVDC',
  '/analytics/test-overview/tests/vdc/passed': 'TestsVDC',
  '/analytics/test-overview/tests/vdc/error': 'TestsVDC',
  '/analytics/test-overview/tests/vdc/complete': 'TestsVDC',
  '/analytics/test-overview/tests/vdc/inconsistent': 'TestsVDC',
  '/analytics/test-overview/tests/rdc': 'TestsRDC',
  '/analytics/test-overview/tests/rdc/all': 'TestsRDC',
  '/analytics/test-overview/tests/rdc/failed': 'TestsRDC',
  '/analytics/test-overview/tests/rdc/passed': 'TestsRDC',
  '/analytics/test-overview/tests/rdc/error': 'TestsRDC',
  '/analytics/test-overview/tests/rdc/complete': 'TestsRDC',
  '/analytics/test-overview/tests/rdc/inconsistent': 'TestsRDC',
  '/analytics/test-overview/errors': 'TestErrorsVDC',
  '/analytics/test-overview/errors/vdc': 'TestErrorsVDC',
  '/analytics/test-overview/errors/rdc': 'TestErrorsRDC',
  '/analytics/test-history': 'TestHistoryTestsVDC',
  '/analytics/test-history/all': 'TestHistoryTestsVDC',
  '/analytics/test-history/failed': 'TestHistoryTestsVDC',
  '/analytics/test-history/passed': 'TestHistoryTestsVDC',
  '/analytics/test-history/error': 'TestHistoryTestsVDC',
  '/analytics/test-history/complete': 'TestHistoryTestsVDC',
  '/analytics/test-history/inconsistent': 'TestHistoryTestsVDC',
  '/analytics/test-history/vdc': 'TestHistoryTestsVDC',
  '/analytics/test-history/vdc/all': 'TestHistoryTestsVDC',
  '/analytics/test-history/vdc/failed': 'TestHistoryTestsVDC',
  '/analytics/test-history/vdc/passed': 'TestHistoryTestsVDC',
  '/analytics/test-history/vdc/error': 'TestHistoryTestsVDC',
  '/analytics/test-history/vdc/complete': 'TestHistoryTestsVDC',
  '/analytics/test-history/vdc/inconsistent': 'TestHistoryTestsVDC',
  '/analytics/test-history/rdc': 'TestHistoryTestsRDC',
  '/analytics/test-history/rdc/all': 'TestHistoryTestsRDC',
  '/analytics/test-history/rdc/failed': 'TestHistoryTestsRDC',
  '/analytics/test-history/rdc/passed': 'TestHistoryTestsRDC',
  '/analytics/test-history/rdc/error': 'TestHistoryTestsRDC',
  '/analytics/test-history/rdc/complete': 'TestHistoryTestsRDC',
  '/analytics/test-history/rdc/inconsistent': 'TestHistoryTestsRDC',
  '/analytics/trends': 'Trends',
  '/analytics/trends/concurrency': 'Concurrency',
});

export const STORAGE_TESTS_KEY = 'TestOverview_Tests_Key';
export const STORAGE_TESTS_RDC_KEY = 'TestOverview_Tests_RDC_Key';

export const SORT_DIRECTION = {
  ASCENDING: 'asc',
  DESCENDING: 'desc',
};

export const OWNER_FILTER_SCOPES_VALUES = Object.values(OWNER_FILTER_SCOPES);
export const MAX_WINDOW_SIZE_FOR_TEST_NAME = 1366;
export const MAX_TEST_NAME_LENGTH = 40;
export const DEFAULT_TEST_NAME_SUFFIX_START = 15;

export const TEST_CASE_PAGE_PATHNAME = 'test-case';
export const DEFAULT_TEST_CASE_SORT = Object.freeze({
  field: 'name',
  direction: SORT_DIRECTION.ASCENDING,
});

export const TEST_CASE_PAGE_PER_PAGE = 25;

export const CONCURRENCY_REPORT_TYPE = Object.freeze({
  vdc: Object.freeze({
    key: 'vdc',
    label: 'Virtual Devices',
  }),
  rdc: Object.freeze({
    key: 'rdc',
    label: 'Real Devices',
  }),
  privateDevice: Object.freeze({
    key: 'private-devices',
    label: 'Private Devices',
  }),
});

export const COVERAGE_TABS = Object.freeze({
  devices: Object.freeze({
    key: 'devices',
    title: 'Devices',
    path: '/analytics/coverage/devices',
    trackEvent: 'DEVICE_COVERAGE',
    store: 'device', // required for computing "empty" state of CSV button
  }),
  browsers: Object.freeze({
    key: 'browsers',
    title: 'Browsers',
    path: '/analytics/coverage/browsers',
    trackEvent: 'BROWSER_COVERAGE',
    store: 'browser', // required for computing "empty" state of CSV button
  }),
  os: Object.freeze({
    key: 'os',
    title: 'OS',
    path: '/analytics/coverage/os',
    trackEvent: 'OS_COVERAGE',
    store: 'os', // required for computing "empty" state of CSV button
  }),
});

export const COVERAGE_TYPES = Object.freeze({
  DEVICE: 'device',
  BROWSER: 'browser',
  OS: 'os',
});

export const DEFAULT_COVERAGE_SORT = Object.freeze({
  field: 'count',
  direction: SORT_DIRECTION.DESCENDING,
});

export const DEVICE_COVERAGE_HEADERS = Object.freeze({
  name: 'Name',
  count: 'Sessions',
  total_duration: 'Total duration',
});

export const TIME_INTERVAL_OPTIONS = Object.freeze({
  hourly: Object.freeze({
    key: 'hourly',
    label: 'Hourly',
    value: '1h',
    timeMS: ONE_HOUR,
    timeFormat: 'd LLL y HH:mm',
  }),
  daily: Object.freeze({
    key: 'daily',
    label: 'Daily',
    value: '1d',
    timeMS: ONE_DAY,
    timeFormat: 'd LLL y',
  }),
  weekly: Object.freeze({
    key: 'weekly',
    label: 'Weekly',
    value: '7d',
    timeMS: 7 * ONE_DAY,
    timeFormat: 'd LLL y',
  }),
});

export const TESTS_CHART_LABELS = Object.freeze({
  passed: {
    key: 'passed',
    color: '#41990f',
    label: 'Pass',
  },
  error: {
    key: 'error',
    color: '#ff8514',
    label: 'Error',
  },
  failed: {
    key: 'failed',
    color: '#ae0f0f',
    label: 'Fail',
  },
  complete: {
    key: 'complete',
    color: '#b6becc',
    label: 'Completed',
  },
});
